<template>
    <div class="conversation-list">
        <!-- Search header -->
        <div class="list-header">
            <div class="search-container">
                <i class="material-icons search-icon">search</i>
                <input
                    type="text"
                    v-model="searchQuery"
                    placeholder="Rechercher une conversation..."
                    class="search-input"
                />
                <button
                    v-if="searchQuery"
                    class="clear-search"
                    @click="searchQuery = ''"
                >
                    <i class="material-icons">close</i>
                </button>
            </div>
        </div>

        <!-- Conversations -->
        <div class="conversations-container" ref="listContainer">
            <!-- Loading state -->
            <div v-if="loading" class="loading-state">
                <div class="spinner"></div>
                <span>Chargement...</span>
            </div>

            <!-- Empty state -->
            <div v-else-if="filteredConversations.length === 0" class="empty-state">
                <i class="material-icons">chat_bubble_outline</i>
                <p v-if="searchQuery">Aucun résultat pour "{{ searchQuery }}"</p>
                <p v-else>Aucune conversation</p>
                <span v-if="!searchQuery">Commencez une nouvelle conversation</span>
            </div>

            <!-- Conversation items -->
            <div v-else class="conversations">
                <ChatConversationItem
                    v-for="conversation in filteredConversations"
                    :key="conversation._id"
                    :conversation="conversation"
                    :current-user-id="currentUserId"
                    :is-active="activeConversationId === conversation._id"
                    :is-online="isUserOnline(getOtherParticipantId(conversation))"
                    :is-typing="isUserTyping(conversation._id)"
                    @select="handleSelect"
                />
            </div>
        </div>

        <!-- New conversation button (for Account Managers) -->
        <div v-if="showNewButton" class="new-conversation">
            <button class="new-btn" @click="$emit('new-conversation')">
                <i class="material-icons">add</i>
                <span>Nouvelle conversation</span>
            </button>
        </div>
    </div>
</template>

<script>
import ChatConversationItem from './ChatConversationItem.vue';

export default {
    name: 'ChatConversationList',
    components: {
        ChatConversationItem
    },
    props: {
        conversations: {
            type: Array,
            default: () => []
        },
        currentUserId: {
            type: String,
            required: true
        },
        activeConversationId: {
            type: String,
            default: null
        },
        onlineUsers: {
            type: [Array, Set, Object],
            default: function() { return {}; }
        },
        typingUsers: {
            type: Object,
            default: () => ({})
        },
        loading: {
            type: Boolean,
            default: false
        },
        showNewButton: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            searchQuery: ''
        };
    },
    computed: {
        filteredConversations() {
            if (!this.searchQuery.trim()) {
                return this.conversations;
            }

            const query = this.searchQuery.toLowerCase().trim();

            return this.conversations.filter(conv => {
                const participant = this.getOtherParticipant(conv);
                if (!participant) return false;

                const name = (
                    participant.fullName ||
                    `${participant.firstName || ''} ${participant.lastName || ''}`
                ).toLowerCase();

                const email = (participant.email || '').toLowerCase();

                return name.includes(query) || email.includes(query);
            });
        },
        onlineUsersSet() {
            if (this.onlineUsers instanceof Set) {
                return this.onlineUsers;
            }
            if (Array.isArray(this.onlineUsers)) {
                return new Set(this.onlineUsers);
            }
            // onlineUsers is an Object { userId: boolean }
            var keys = Object.keys(this.onlineUsers || {});
            var onlineKeys = keys.filter(function(key) {
                return this.onlineUsers[key] === true;
            }.bind(this));
            return new Set(onlineKeys);
        }
    },
    methods: {
        getOtherParticipant(conversation) {
            const { seller, accountManager } = conversation;

            if (seller && seller._id === this.currentUserId) {
                return accountManager;
            }
            return seller;
        },
        getOtherParticipantId(conversation) {
            const participant = this.getOtherParticipant(conversation);
            return (participant && participant._id) || null;
        },
        isUserOnline(userId) {
            if (!userId) return false;
            return this.onlineUsersSet.has(userId);
        },
        isUserTyping(conversationId) {
            var typingData = this.typingUsers[conversationId];
            if (!typingData) return false;

            // Handle both Array and Object formats
            if (Array.isArray(typingData)) {
                if (typingData.length === 0) return false;
                var self = this;
                return typingData.some(function(userId) { return userId !== self.currentUserId; });
            }

            // typingData is an Object { userId: boolean }
            var keys = Object.keys(typingData);
            var self = this;
            return keys.some(function(userId) {
                return typingData[userId] === true && userId !== self.currentUserId;
            });
        },
        handleSelect(conversation) {
            this.$emit('select', conversation);
        }
    }
};
</script>

<style scoped>
.conversation-list {
    display: flex;
    flex-direction: column;
    height: 100%;
    background-color: #ffffff;
}

.list-header {
    padding: 16px;
    border-bottom: 1px solid #e5e7eb;
}

.search-container {
    position: relative;
    display: flex;
    align-items: center;
}

.search-icon {
    position: absolute;
    left: 12px;
    color: #9ca3af;
    font-size: 20px;
}

.search-input {
    width: 100%;
    padding: 10px 36px;
    border: 1px solid #e5e7eb;
    border-radius: 24px;
    font-size: 14px;
    outline: none;
    transition: all 0.2s;
}

.search-input:focus {
    border-color: #059669;
    box-shadow: 0 0 0 3px rgba(5, 150, 105, 0.1);
}

.search-input::placeholder {
    color: #9ca3af;
}

.clear-search {
    position: absolute;
    right: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border: none;
    background: none;
    cursor: pointer;
    color: #9ca3af;
    border-radius: 50%;
    transition: all 0.2s;
}

.clear-search:hover {
    background-color: #f3f4f6;
    color: #6b7280;
}

.clear-search i {
    font-size: 18px;
}

.conversations-container {
    flex: 1;
    overflow-y: auto;
}

.loading-state,
.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 200px;
    color: #9ca3af;
    text-align: center;
    padding: 20px;
}

.spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #e5e7eb;
    border-top-color: #059669;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin-bottom: 12px;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.empty-state i {
    font-size: 48px;
    margin-bottom: 12px;
    opacity: 0.5;
}

.empty-state p {
    margin: 0;
    font-size: 14px;
    font-weight: 500;
    color: #6b7280;
}

.empty-state span {
    font-size: 13px;
    margin-top: 4px;
}

.conversations {
    padding: 8px 0;
}

.new-conversation {
    padding: 16px;
    border-top: 1px solid #e5e7eb;
}

.new-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 12px;
    background: linear-gradient(135deg, #059669 0%, #10b981 100%);
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
}

.new-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(5, 150, 105, 0.3);
}

.new-btn i {
    font-size: 20px;
}
</style>
